import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import breakPoints from 'helpers/Styles';
import InputField from 'components/Form/InputField';
import SubmitButton from 'components/Form/SubmitButton';
import FormSubmitErrors from 'components/Form/FormSubmitErrors';
import {
  CUSTOMER_UPDATE_MUTATION,
  customerUpdateVars,
  CUSTOMER_ADDRESS_UPDATE_MUTATION,
  customerAddressUpdateVars,
  CUSTOMER_ADDRESS_CREATE_MUTATION,
  customerAddressCreateVars,
  CUSTOMER_DEFAULT_ADDRESS_UPDATE_MUTATION,
  customerDefaultAddressUpdateVars,
} from 'helpers/customer';
import { phone } from 'phone';
import PrimaryButton from '../PrimaryButton';

const EditDetails = ({ customer, token, handleEdit, customerRefetch }) => {
  const errorsRef = useRef()
  const [submitted, setSubmitted] = useState(false)

  const [customerUpdate, { error, data }] = useMutation(
    CUSTOMER_UPDATE_MUTATION,
  );
  const [customerAddressUpdate, { error: addressError, data: addressData }] = useMutation(
    CUSTOMER_ADDRESS_UPDATE_MUTATION,
  );
  const [customerAddressCreate, { error: addressCreateError, data: addressCreateData }] =
    useMutation(CUSTOMER_ADDRESS_CREATE_MUTATION);
  const [
    customerDefaultAddressUpdate,
    { error: addressDefaultUpdateError, data: addressDefaultUpdateData },
  ] = useMutation(CUSTOMER_DEFAULT_ADDRESS_UPDATE_MUTATION);

  const [detailsUpdatedMessage, setDetailsUpdatedMessage] = useState(false);

  const onSubmit = (values, { setSubmitting }) => {
    if (values?.phone) {
      values.phone = phone(values.phone, { country: 'MY' }).phoneNumber;
    }
    customerUpdate({
      variables: customerUpdateVars(token, values),

      update(proxy, { data: { customerUpdate } }) {
        setSubmitting(false);
      },
    });
    const addressUpdateData = {
      address1: values.address1,
      city: values.city,
      province: values.province,
      zip: values.zip,
      country: 'Malaysia',
    };
    if (customer?.defaultAddress?.id) {
       
      customerAddressUpdate({
        variables: customerAddressUpdateVars(
          token,
          customer.defaultAddress?.id,
          addressUpdateData,
        ),
        update(proxy, { data: { customerAddressUpdate } }) {
           
          if (customerAddressUpdate.customerAddress) {
            customerRefetch();
          }
          setSubmitting(false);
        },
      });
    } else {
      customerAddressCreate({
        variables: customerAddressCreateVars(token, addressUpdateData),
        update(proxy, { data: { customerAddressCreate } }) {
          if (customerAddressCreate.customerAddress) {
            customerDefaultAddressUpdate({
              variables: customerDefaultAddressUpdateVars(
                token,
                customerAddressCreate.customerAddress.id,
              ),
              update(proxy, { data: { customerDefaultAddressUpdate } }) {
                customerRefetch();
                setSubmitting(false);
              },
            });
          }
        },
      });
    }

    setSubmitted(true)
  };

  useEffect(() => {
    //simple way to check for errors is to see if errors container has any height
    if(submitted){
      if(errorsRef.current.offsetHeight > 0){
        setDetailsUpdatedMessage(false)
      }
      else setDetailsUpdatedMessage(true)
    }


  }, [data, addressData, addressCreateData, addressDefaultUpdateData])

  const buttonLabel = 'Update';
  return (
    <>
      <div className="my_details">
        {detailsUpdatedMessage ? (
          <p className="successful_message">
            Your details have been successfully updated.
          </p>
        ) : null}
        <div className="edit_form_container">
          <Formik
            initialValues={{
              email: customer.email,
              firstName: customer.firstName,
              lastName: customer.lastName,
              phone: customer.phone,
              address1: customer.defaultAddress?.address1,
              city: customer.defaultAddress?.city,
              province: customer.defaultAddress?.province,
              zip: customer.defaultAddress?.zip,
            }}
            validationSchema={Yup.object({
              email: Yup.string()
                .email('Invalid email address')
                .required('Required Email'),
              firstName: Yup.string().required('Required First Name'),
              lastName: Yup.string().required('Required Last Name'),
              phone: Yup.number().required('Required Phonenumber'),
              address1: Yup.string().required('Required Address'),
              city: Yup.string().required('Required Suburb'),
              province: Yup.string().required('Required State/Region'),
              zip: Yup.number().required('Required Postcode').positive().integer(),
            })}
            onSubmit={onSubmit}
            validate={(values) => {
              let errors = {};
              values.phone = phone(values.phone, { country: 'MY' }).phoneNumber;
              if (values.phone == null) {
                errors.phone = "Invalid phone number. Please input MY phone number (+60).";
              }
              return errors;
            }}
          >
            {({ isSubmitting, isValid, dirty }) => (
              <div className="formContainer">
                <div className="errors-container" ref={errorsRef}>
                  {data && data.customerUpdate.customerUserErrors ? (
                    <FormSubmitErrors
                      errors={data.customerUpdate.customerUserErrors}
                    />
                  ) : null}
                  {addressCreateData &&
                  addressCreateData.customerAddressCreate.customerUserErrors ? (
                    <FormSubmitErrors
                      errors={
                        addressCreateData.customerAddressCreate
                          .customerUserErrors
                      }
                    />
                  ) : null}
                  {addressData &&
                  addressData.customerAddressUpdate.customerUserErrors ? (
                    <FormSubmitErrors
                      errors={
                        addressData.customerAddressUpdate.customerUserErrors
                      }
                    />
                  ) : null}
                  {addressDefaultUpdateData &&
                  addressDefaultUpdateData.customerAddressUpdate &&
                  addressDefaultUpdateData.customerAddressUpdate
                    .customerUserErrors ? (
                    <FormSubmitErrors
                      errors={
                        addressDefaultUpdateData.customerDefaultAddressUpdate
                          .customerUserErrors
                      }
                    />
                  ) : null}
                  {error ? (
                    <FormSubmitErrors
                      errors={[
                        { message: 'Something went wrong. Please try again.' },
                      ]}
                    />
                  ) : null}
                </div>
                <Form>
                  <InputField
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    autoFocus
                  />
                  <InputField
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                  />
                  <InputField
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    autoComplete="username"
                  />
                  <InputField
                    type="text"
                    name="phone"
                    placeholder="Phone number"
                    autoComplete="phone"
                  />
                  <InputField
                    type="text"
                    name="address1"
                    placeholder="Address"
                    autoComplete="address1"
                  />
                  <InputField
                    type="text"
                    name="city"
                    placeholder="Suburb"
                    autoComplete="city"
                  />
                  <InputField
                    type="text"
                    name="province"
                    placeholder="State/Region"
                    autoComplete="province"
                  />
                  <InputField
                    type="text"
                    name="zip"
                    placeholder="Postcode"
                    autoComplete="zip"
                  />

                  <div className="buttons_container">
                    <PrimaryButton
                      text={'CANCEL'}
                      onClick={() => handleEdit(false)}
                    />
                    <SubmitButton
                      // isSubmitting={isSubmitting}
                      label={buttonLabel}
                      submittingLabel="Updating..."
                    />
                  </div>
                </Form>
              </div>
            )}
          </Formik>
        </div>
        <style jsx>
          {`
            .edit_form_container {
              max-width: 640px;
              margin-top: 40px;
            }
            .buttons_container {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 10px;
            }
            .my_details {
              min-width: 368px;
            }
            .details_title {
              color: #e4e4e4;
              font-family: 'big_noodle_titling';
              font-size: 31px;
              letter-spacing: 0;
              line-height: 40px;
              margin-bottom: 50px;
              text-align: left;
            }
            .signup__description {
              margin-top: 44px;
              margin-bottom: 52px;
              font-size: 0.9em;
            }
            .extraAction {
              display: block;
              margin-top: 30px;
              color: #757575;
            }
            .formContainer,
            h1,
            p {
              margin: 0 auto;
              text-align: center;
              margin-bottom: 40px;
            }
            .successful_message {
              color: #e4e4e4;
              font-family: 'Roboto-Medium';
              font-size: 16px;
              letter-spacing: 0;
              line-height: 24px;
              text-align: center;
            }
            @media (max-width: ${breakPoints.small}) {
              .my_details {
                width: 100%;
              }
              .react-tabs__tab-list {
                min-height: 200px;
              }
              .tabpanel--container {
                padding-left: 0;
              }
            }
          `}
        </style>
      </div>
    </>
  );
};

EditDetails.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  customer: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export default EditDetails;
